/**
 * ч.843: віджети головної — борги (дебіторка/кредиторка), дні народження,
 * податковий календар. Дані — GET /dashboard/* (read-only, доступні org'и).
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboard } from '@/api/dashboard';
import { useOrgFilter } from '@/store/defaults';
import { debtReportLinks } from '@/pages/reportLanding';
import DashboardWidget from '../DashboardWidget';

const fmtMoney = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' });

/** Борги: дебіторка або кредиторка — сальдо на сьогодні + топ-3 контрагентів.
 *
 *  ч.1035 (рішення власника): сума — з проводок по всіх рахунках розрахунків
 *  (те, що в акті звірки й ОСВ), а рядком нижче — «прострочено за
 *  накладними» з розрізу по документах (строки оплати є лише там). Джерело
 *  суми НЕ міняємо на розріз по документах: він не бачить позик, авансів,
 *  актів послуг і довідок 1С — занижував би борг (ГАЛІС: −2,6 млн по 63). */
export function DebtWidget({ kind }: { kind: 'receivable' | 'payable' }) {
  const navigate = useNavigate();
  const isRecv = kind === 'receivable';
  const [orgId] = useOrgFilter();
  const links = debtReportLinks(kind, orgId || undefined);
  const q = useQuery({
    queryKey: ['dash-debts', kind, orgId],
    // ч.844: топ-3 боржники (як і решта віджетів), організація — з фільтра шапки.
    queryFn: () => dashboard.debts({ kind, top: 3, organization_id: orgId || undefined }),
    staleTime: 120_000,
  });
  const d = q.data;
  return (
    <DashboardWidget
      title={isRecv ? 'Борг дебет' : 'Борг кредит'}
      loading={q.isLoading}
      isEmpty={!q.isLoading && (!d || Number(d.total) === 0)}
      emptyMessage="Заборгованості немає"
      actions={<Link to={links.all} style={{ fontSize: '0.82rem' }}
        title="Монітор розрахунків: усі контрагенти з боргом">усі →</Link>}
    >
      {d && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: '1.4rem', fontWeight: 600,
              color: isRecv ? 'var(--badge-blue-fg)' : 'var(--badge-amber-fg)' }}>
              {fmtMoney(d.total)}
            </span>
            <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
              грн · {d.count} контрагент(ів)
            </span>
          </div>
          <div style={{ fontSize: '0.82rem', marginBottom: 6 }}>
            {Number(d.overdue) > 0 ? (
              <Link to={links.overdue}
                style={{ color: 'var(--badge-red-fg)', fontWeight: 600 }}
                title="За накладними зі строком оплати з договору (відстрочка). Позики, аванси, акти без строку сюди не входять — відкрити «Розрахунки за документами»">
                прострочено за накладними: {fmtMoney(d.overdue)} грн · {d.overdue_docs} док.
              </Link>
            ) : (
              <span style={{ color: 'var(--muted)' }}
                title="Серед накладних зі строком оплати з договору прострочених немає">
                прострочених накладних немає
              </span>
            )}
          </div>
          {d.top.map((t) => (
            <div key={t.contractor_id}
              onClick={() => navigate(`/contractors/${t.contractor_id}`)}
              style={{ display: 'flex', gap: 8, padding: '3px 0', cursor: 'pointer',
                borderBottom: '1px solid var(--border-soft)', fontSize: '0.85rem' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.name}
              </span>
              <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{fmtMoney(t.amount)}</span>
            </div>
          ))}
        </>
      )}
    </DashboardWidget>
  );
}

/** Дні народження: сьогодні + найближчі 2 дні. */
export function BirthdaysWidget() {
  const [orgId] = useOrgFilter();
  const q = useQuery({
    queryKey: ['dash-birthdays', orgId],
    queryFn: () => dashboard.birthdays({ days: 2, limit: 10, organization_id: orgId || undefined }),
    staleTime: 600_000,
  });
  const items = q.data?.items ?? [];
  return (
    <DashboardWidget
      title="Дні народження"
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Найближчими днями немає"
    >
      {items.map((b) => (
        <div key={b.employee_id}
          style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0',
            borderBottom: '1px solid var(--border-soft, #f1f5f9)', fontSize: '0.85rem' }}>
          <span style={{ whiteSpace: 'nowrap', minWidth: 64,
            color: b.in_days === 0 ? '#16a34a' : 'var(--muted)',
            fontWeight: b.in_days === 0 ? 600 : 400 }}>
            {b.in_days === 0 ? 'сьогодні' : fmtDay(b.date)}
          </span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={b.position_name ?? undefined}>
            {b.name}
          </span>
          {b.turns_age != null && (
            <span style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>{b.turns_age} р.</span>
          )}
        </div>
      ))}
    </DashboardWidget>
  );
}

const KIND_COLOR: Record<string, string> = {
  payment: '#b45309', report: '#0369a1', payroll: '#7c3aed',
};

/** Податковий календар: найближчі дедлайни по організаціях тенанта. */
export function TaxCalendarWidget() {
  const [orgId] = useOrgFilter();
  const q = useQuery({
    queryKey: ['dash-tax-calendar', orgId],
    queryFn: () => dashboard.taxCalendar({ days: 30, limit: 3, organization_id: orgId || undefined }),
    staleTime: 600_000,
  });
  const items = q.data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);
  return (
    <DashboardWidget
      title="Податковий календар"
      loading={q.isLoading}
      isEmpty={!q.isLoading && items.length === 0}
      emptyMessage="Найближчих 30 днів дедлайнів немає"
    >
      {items.map((e, i) => (
        <div key={`${e.date}:${e.organization_id}:${i}`}
          style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0',
            borderBottom: '1px solid var(--border-soft, #f1f5f9)', fontSize: '0.85rem' }}>
          <span style={{ whiteSpace: 'nowrap', minWidth: 48,
            color: e.date <= today ? '#dc2626' : 'var(--muted)',
            fontWeight: e.date <= today ? 600 : 400 }}>
            {fmtDay(e.date)}
          </span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={`${e.organization_name}${e.note ? ` · ${e.note}` : ''}`}>
            <span style={{ color: KIND_COLOR[e.kind] ?? undefined }}>{e.title}</span>
            {e.note && <span style={{ color: 'var(--muted)' }}> · {e.note}</span>}
          </span>
        </div>
      ))}
    </DashboardWidget>
  );
}
